import { useEffect, useState } from "react";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PaginationBar } from "@/components/shared/PaginationBar";
import { usePaymentDeskRefData } from "./shared/usePaymentDeskRefData";
import LineFormDialog from "./LineFormDialog";
import { EditHistoryDialog } from "./shared/EditHistoryDialog";
import { PayeeCell } from "./shared/PayeeCell";
import { DeleteEntryDialog } from "./shared/DeleteEntryDialog";
import { money } from "./PaymentListDetail";
import { Pencil, History, Paperclip, Trash2 } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}-${m}-${y}`;
}

// Every RTGS/NEFT line with status Paid. Non-Directors can correct a line
// within 15 days of its created date; the Director has no window.
// Enforced by the backend — the disabled Edit button is only a hint.
export default function CompletedPaymentsTab() {
  const refData = usePaymentDeskRefData();
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editTarget, setEditTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const load = (pageNum = 1) => {
    setLoading(true);
    client
      .get("/payment-desk/completed-payments", { params: { page: pageNum, page_size: 50 } })
      .then((res) => {
        setLines(res.data.items);
        setPage(res.data.page);
        setTotalPages(res.data.total_pages);
      })
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(1); }, []);

  const viewAttachment = async (line) => {
    try {
      const res = await client.get(`/payment-desk/lines/${line.id}/attachment`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-4" data-testid="completed-payments-tab">
      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <Table data-testid="completed-payments-table">
          <TableHeader>
            <TableRow>
              <TableHead>Entity</TableHead>
              <TableHead>Site / Equipment</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Vendor</TableHead>
              <TableHead>Paid To</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Payment Mode</TableHead>
              <TableHead>Paid Date</TableHead>
              <TableHead>UTR</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={10} className="text-center py-8 text-slate-400">Loading...</TableCell></TableRow>
            ) : lines.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center py-8 text-slate-400" data-testid="completed-payments-empty">No completed RTGS/NEFT payments yet.</TableCell></TableRow>
            ) : (
              lines.map((line) => (
                <TableRow key={line.id} data-testid={`completed-line-row-${line.id}`}>
                  <TableCell className="text-xs">{line.entity_name || line.entity_code}</TableCell>
                  <TableCell className="text-xs">{line.site_label || line.equipment_label}</TableCell>
                  <TableCell className="text-xs">{line.category_name}</TableCell>
                  <TableCell className="text-xs">{line.vendor_name || "—"}</TableCell>
                  <TableCell><PayeeCell line={line} /></TableCell>
                  <TableCell data-testid={`completed-line-amount-${line.id}`}>{money(line.amount)}</TableCell>
                  <TableCell className="text-xs">{line.payment_mode}</TableCell>
                  <TableCell className="text-xs whitespace-nowrap" data-testid={`completed-line-paid-date-${line.id}`}>{fmtDate(line.paid_date)}</TableCell>
                  <TableCell className="font-mono text-xs" data-testid={`completed-line-utr-${line.id}`}>{line.utr || "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      {line.attachment && (
                        <Button size="sm" variant="outline" title={line.attachment.filename} data-testid={`completed-line-attachment-${line.id}`} onClick={() => viewAttachment(line)}>
                          <Paperclip className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button size="sm" variant="outline" data-testid={`completed-line-history-${line.id}`} onClick={() => setHistoryTarget(line)}>
                        <History className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!line.can_correct || !refData.entities.length}
                        data-testid={`completed-line-edit-${line.id}`}
                        onClick={() => setEditTarget(line)}
                      >
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                      </Button>
                      {line.can_delete && (
                        <Button size="sm" variant="destructive" data-testid={`completed-line-delete-${line.id}`} onClick={() => setDeleteTarget(line)}>
                          <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                        </Button>
                      )}
                    </div>
                    {!line.can_correct && (
                      <p className="text-[10px] text-slate-400 mt-1" data-testid={`completed-line-window-closed-${line.id}`}>
                        Edit window closed ({fmtDate(line.edit_deadline)})
                      </p>
                    )}
                    {line.can_correct && line.is_director_override && (
                      <p className="text-[10px] text-amber-600 mt-1" data-testid={`completed-line-director-override-${line.id}`}>
                        Window closed — Director override
                      </p>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <PaginationBar page={page} totalPages={totalPages} onPageChange={load} testId="completed-payments-pagination" />

      {editTarget && (
        <LineFormDialog
          correctionMode
          line={editTarget}
          refData={refData}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); load(page); }}
        />
      )}
      {historyTarget && <EditHistoryDialog line={historyTarget} onClose={() => setHistoryTarget(null)} />}
      {deleteTarget && (
        <DeleteEntryDialog
          title={`Delete ${deleteTarget.list_id} · Line ${deleteTarget.line_no}?`}
          endpoint={`/payment-desk/lines/${deleteTarget.id}/delete`}
          details={[
            ["Payment List", deleteTarget.list_id],
            ["Paid To", `${deleteTarget.payee_type}${deleteTarget.payee_name ? ` — ${deleteTarget.payee_name}` : ""}`],
            ["Amount", money(deleteTarget.amount)],
            ["Status", deleteTarget.status],
          ]}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => { setDeleteTarget(null); load(page); }}
        />
      )}
    </div>
  );
}
